import type { Variants } from '@amplitude/experiment-js-client';

import { Deployment } from '@liveauctioneers/hammer-ui-payments/types';

import { getKnownVariantValues } from '../AmplitudeExperiments';

export const FORCE_EXPERIMENT_QUERY_PARAM = 'forceExperiment';
export const FORCED_EXPERIMENT_OVERRIDES_STORAGE_KEY = 'la:forcedExperimentOverrides';

const CLEAR_OVERRIDES_VALUE = 'clear';

const isKnownFlagVariant = (flagKey: string, variantValue: string): boolean => {
    const knownVariantValues = getKnownVariantValues(flagKey);

    if (!knownVariantValues) {
        console.error(`forceExperiment: unknown flag key "${flagKey}", ignoring`);
        return false;
    }

    if (!knownVariantValues.includes(variantValue)) {
        console.error(`forceExperiment: unknown variant "${variantValue}" for flag "${flagKey}", ignoring`);
        return false;
    }

    return true;
};

/**
 * Validates an untrusted candidate (parsed query param or hand-editable sessionStorage JSON)
 * down to overrides whose flag keys and variant values exist in the experiment registry.
 */
const sanitizeOverrides = (candidate: unknown): Variants => {
    if (!candidate || typeof candidate !== 'object') {
        return {};
    }

    const sanitizedOverrides: Variants = {};

    Object.entries(candidate).forEach(([flagKey, variant]) => {
        const variantValue: unknown = variant && typeof variant === 'object' ? variant.value : undefined;

        if (typeof variantValue !== 'string' || !isKnownFlagVariant(flagKey, variantValue)) {
            return;
        }

        sanitizedOverrides[flagKey] = { value: variantValue };
    });

    return sanitizedOverrides;
};

const readStoredOverrides = (): Variants => {
    const storedOverridesJson = window.sessionStorage.getItem(FORCED_EXPERIMENT_OVERRIDES_STORAGE_KEY);

    if (!storedOverridesJson) {
        return {};
    }

    return sanitizeOverrides(JSON.parse(storedOverridesJson));
};

const parseQueryOverrides = (queryParamEntries: string[]): Variants => {
    const candidateOverrides: Variants = {};

    queryParamEntries
        .flatMap((queryParamEntry) => queryParamEntry.split(','))
        .map((overrideEntry) => overrideEntry.trim())
        .filter((overrideEntry) => overrideEntry.length > 0)
        .forEach((overrideEntry) => {
            // split on the FIRST colon only, so a variant value that itself contains ':' is preserved
            const colonIndex = overrideEntry.indexOf(':');

            if (colonIndex <= 0 || colonIndex === overrideEntry.length - 1) {
                console.error(`forceExperiment: expected "<flagKey>:<variant>" but got "${overrideEntry}", ignoring`);
                return;
            }

            const flagKey = overrideEntry.slice(0, colonIndex);
            const variantValue = overrideEntry.slice(colonIndex + 1);

            candidateOverrides[flagKey] = { value: variantValue };
        });

    return sanitizeOverrides(candidateOverrides);
};

export type DevOverrideDeployment = Deployment | 'test' | '';

/** Dev-tools overrides (forceExperiment, flag cookies) are off on prod and allowed everywhere else. */
export const areDevOverridesAllowed = (deployment: DevOverrideDeployment): boolean => deployment !== Deployment.Prod;

/**
 * QA / dev tool: reads experiment variant overrides forced via
 * `?forceExperiment=<flagKey>:<variant>` (repeatable or comma-separated) and persists them to
 * sessionStorage so they survive in-app navigation for the rest of the tab's session.
 * `?forceExperiment=clear` (or an empty value) drops all stored overrides.
 *
 * Inert in production. `deployment` comes from server boot config (Fastify -> Redux bridge), so the guard cannot be
 * spoofed from the browser.
 */
export const getForcedExperimentVariantOverrides = (deployment: DevOverrideDeployment): Variants => {
    if (!areDevOverridesAllowed(deployment)) {
        return {};
    }

    if (typeof window === 'undefined') {
        return {};
    }

    try {
        const queryParamEntries = new URLSearchParams(window.location.search).getAll(FORCE_EXPERIMENT_QUERY_PARAM);

        if (queryParamEntries.some((entry) => entry === '' || entry === CLEAR_OVERRIDES_VALUE)) {
            window.sessionStorage.removeItem(FORCED_EXPERIMENT_OVERRIDES_STORAGE_KEY);
            return {};
        }

        const storedOverrides = readStoredOverrides();

        if (queryParamEntries.length === 0) {
            return storedOverrides;
        }

        const mergedOverrides = { ...storedOverrides, ...parseQueryOverrides(queryParamEntries) };
        window.sessionStorage.setItem(FORCED_EXPERIMENT_OVERRIDES_STORAGE_KEY, JSON.stringify(mergedOverrides));

        return mergedOverrides;
    } catch (error) {
        // QA tool, must never break the app (sessionStorage access can throw, stored JSON can be corrupt)
        console.error('forceExperiment: failed to read overrides, ignoring', error);
        return {};
    }
};
